import type { BookingDetail } from '@/shared/api'
import { SectionCard } from '@/shared/ui'
import s from './BookingPlatformCard.module.css'

type PlatformReport = NonNullable<BookingDetail['platformReport']>

/**
 * Секция «Данные источника» — то, что источник сообщил про эту бронь.
 *
 * ⚠️ ОТДЕЛЬНАЯ СЕКЦИЯ, А НЕ СТРОКИ ВНУТРИ «ПЛАТЕЖЕЙ»: там состояния НАШЕГО платёжного домена (что
 * мы провели и чем это подтверждено), здесь — пересказ чужих слов («площадка говорит, что приняла
 * 12 000 ₽»). Положив второе в первое, мы выдали бы чужое утверждение за собственный платёжный факт.
 * Письмо бекенда рисует это одним блоком «Оплата» — расходимся сознательно: общая рамка стёрла бы ту
 * границу, ради которой секция и заведена.
 *
 * ⚠️ Строка атрибуции НЕ ССЫЛАЕТСЯ НА РАСПОЛОЖЕНИЕ («в секции выше»): указание на соседа переживёт
 * первую же перестановку секций и станет ложью молча.
 *
 * ⚠️ ОТСУТСТВУЮЩАЯ СТРОКА И «0 ₽» — РАЗНОЕ, И РАЗЛИЧАЕТ ИХ СЕРВЕР. У домена дословно: «`optional`
 * означает „источник про это не сказал", и это НЕ то же самое, что ноль: ноль предоплаты — факт,
 * отсутствие — незнание». В контракте это выражено САМИМ СПИСКОМ: не сказала — строки нет, сказала
 * «ноль» — строка есть. Поэтому здесь нет ни одной ветки про пустоту сумм: ошибку «нарисовать
 * пропуск нулём» этой формой нечем совершить.
 */
export function BookingPlatformCard({ report }: { report: PlatformReport }) {
  /*
   * ⚠️ ПУСТОЙ ОТЧЁТ ЗАКОНЕН — ЭТО ВЕТКА ДАННЫХ, А НЕ ЗАЩИТА ГРАНИЦЫ. Поля нет, только когда нет самой
   * записи источника, и это единственное условие; а у брони, заведённой руками, запись есть и пуста
   * целиком: строк нет, `collectedByLabel` отсутствует при доменном умолчании `unknown`, `externalId`
   * у ручной брони не приходит. Без проверки такая бронь получила бы рамку с заголовком и одной
   * строкой атрибуции.
   *
   * Сид состояния не даёт: у каждой из пяти раскладок `platformReportOf` есть строка или пометка.
   */
  const hasContent =
    report.money.length > 0 ||
    Boolean(report.collectedByLabel) ||
    Boolean(report.externalUrl) ||
    Boolean(report.externalId) ||
    Boolean(report.hiddenNote)

  if (!hasContent) return null

  /*
   * ⚠️ «ИСТОЧНИК», А НЕ «ПЛОЩАДКА», И ЭТО НЕ СИНОНИМ: секция приходит и к брони, у которой площадка
   * НЕ НАЗВАНА — пустой код происхождения гасит только имя, а суммы, способ оплаты, ссылку и пометку
   * источник присылает как обычно. Заголовок «Данные площадки» стоял бы в двух сантиметрах от строки
   * «Источник — не записан». Поймал СКРИНШОТ: зонд об этом не спрашивали, гейты такого не видят.
   *
   * Подписи денежных строк остаются серверными («Комиссия площадки»): это слова команды домена о
   * конкретной сумме, и переписывать их значило бы завести приватный словарь.
   */
  return (
    <SectionCard className={s.card} eyebrow="Данные источника">
      <div className={s.body}>
        <p className={s.lead}>Так эту бронь видит источник — это его слова, а не проведённые нами платежи.</p>

        {/*
         * Фраза серверная: причина зависит от провайдера и известна ему, а домен называет её ПРИМЕРОМ,
         * а не единственной — своя копия утверждала бы одну причину как единственную.
         *
         * ⚠️ ПРЯЧЕТ ПРОВАЙДЕР, А НЕ ПЛОЩАДКА: у домена площадка стоит в ПРИЧИНЕ («RC: площадка не
         * оплачена Channel Manager»). Та же пара объясняет и запасную подпись ссылки ниже: назвать в
         * ней площадку значило бы повторить ровно эту подмену.
         *
         * Тон — в фоне, текст остаётся чернилами: цветной текст этого тона не берёт AA в светлой теме
         * (замер — HANDOFF, «Погасить состояние ВНИЗ по шкале чернил нечем»).
         */}
        {report.hiddenNote && <p className={s.hidden}>{report.hiddenNote}</p>}

        {report.money.length > 0 && (
          <dl className={s.money}>
            {report.money.map((row) => (
              // Ключ — машинный идентификатор строки: подпись сервер вправе переименовать.
              <div key={row.key} className={s.row}>
                <dt className={s.label}>{row.label}</dt>
                <dd className={s.amount}>{row.amountLabel}</dd>
              </div>
            ))}
          </dl>
        )}

        {/*
         * Кто принимает оплату. Поля нет при `unknown`, а `unknown` у домена это УМОЛЧАНИЕ — «площадка
         * не сказала», а не шестой способ. Поэтому ветки «неизвестно» здесь нет.
         */}
        {report.collectedByLabel && <p className={s.collected}>{report.collectedByLabel}</p>}

        {/*
         * ⚠️ АДРЕС ВНЕШНИЙ И НЕДОВЕРЕННЫЙ: `rel="noopener noreferrer"` обязателен — без него открытая
         * вкладка получает `window.opener` и может переписать нашу страницу на свою (tabnabbing).
         * Схему `javascript:` гасит сам React (`sanitizeURL`); своего фильтра не заводим — вторая
         * копия защиты однажды разойдётся с первой. Тот же разбор стоит у ссылки в «Деталях брони».
         *
         * ⚠️ ПОДПИСЬ НАЗЫВАЕТ ПРОВАЙДЕРА, КОГДА СЕРВЕР ЕГО НАЗВАЛ, И ОБЕЗЛИЧЕНА, КОГДА НЕТ. Гарантия
         * пришла КОДОМ (ответ на письмо №16 §2): конвертер перешёл на `Names.Lookup`, и имя приходит
         * пустым, а не суррогатом из первых восьми символов UUID. Обезличенная подпись остаётся ровно
         * для этого случая — пустое имя они назвали законным состоянием.
         */}
        {report.externalUrl && (
          <a className={s.external} href={report.externalUrl} target="_blank" rel="noopener noreferrer">
            {report.externalLabel ?? 'Открыть оригинал брони'}
          </a>
        )}

        {/*
         * ⚠️ КАК БРОНЬ НАЗЫВАЕТСЯ У ИСТОЧНИКА — И ИМЕННО ТАК, А НЕ «НОМЕР БРОНИ»: их совет по §8
         * дословно — «менеджер будет искать его у Авито и не найдёт», своего номера Авито им не
         * сообщает. Место тоже назвали они: «мелким служебным текстом рядом с кнопкой на провайдера
         * либо не показывать вовсе» — из двух разрешённых берём первое. Стоит ПОСЛЕ кнопки и не зависит от неё: идентификатор есть всегда, а ссылку провайдер
         * присылает не в каждом снапшоте.
         *
         * ⚠️ «У ПРОВАЙДЕРА», А НЕ «У ИСТОЧНИКА»: «источник» в приложении это ПЛОЩАДКА, и строка
         * «Источник · Авито · через RealtyCalendar» стоит на том же экране чуть ниже. «У источника»
         * отправило бы менеджера искать номер именно у Авито — ровно тот исход, ради которого §8 и
         * запрещает называть его номером брони.
         */}
        {report.externalId && (
          <p className={s.externalId}>
            У провайдера: <span className={s.externalIdValue}>{report.externalId}</span>
          </p>
        )}
      </div>
    </SectionCard>
  )
}
